import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Search, X, CheckCircle2, XCircle, Clock, Users, Link2, Copy, MailPlus, Ban } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import MyEventSidebar from "./MyEventSidebar";

const TECHNICAL_RE = /^(test[_\s-]|iter\d+|fixture|demo[_\s-]|debug|__)/i;
const cleanName = (s) => {
  if (!s) return "Votre mariage";
  const c = String(s).replace(/^\s*(test[_\s-]+|demo[_\s-]+)/i, "").replace(/\s+[a-f0-9]{6,}$/i, "").trim();
  return TECHNICAL_RE.test(c) ? "Votre mariage" : c;
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; } };

const RSVP_LABELS = { pending: "En attente", confirmed: "Confirmé", declined: "Refusé" };
const RSVP_COLORS = {
  pending:   { chip: "text-amber-300 border-amber-300/40 bg-amber-300/5",  icon: Clock },
  confirmed: { chip: "text-[#DCB457] border-[#DCB457]/50 bg-[#DCB457]/10", icon: CheckCircle2 },
  declined:  { chip: "text-red-300 border-red-300/40 bg-red-300/5",         icon: XCircle },
};

const EMPTY = { prenom: "", nom: "", email: "", phone: "", rsvp_status: "pending", adults: 1, children: 0, category: "", notes: "" };

export default function MyEventGuests() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const [guests, setGuests] = useState([]);
  const [stats, setStats] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [q, setQ] = useState("");
  const [rsvpFilter, setRsvpFilter] = useState("");
  const [editing, setEditing] = useState(null); // id or "new"
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [linkGuestId, setLinkGuestId] = useState(null); // guest_id whose "link" popover is open
  const [linkLoading, setLinkLoading] = useState(false);
  const [lastLink, setLastLink] = useState(null); // { guest_id, url, expires_at, email_sent }

  const generateLink = async (g, sendEmail) => {
    setLinkLoading(true);
    try {
      const r = await api.post(`/next/myevent/${eventId}/guests/${g.id}/magic-link`, { send_email: !!sendEmail });
      setLastLink({ guest_id: g.id, ...r.data });
      if (sendEmail) {
        toast.success(r.data?.email_sent ? "Lien envoyé par email." : "Lien généré (email non envoyé — vérifiez la config).");
      } else {
        toast.success("Lien généré.");
      }
      // Refresh guest to see magic_token_* fields
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de générer le lien.");
    } finally { setLinkLoading(false); }
  };

  const revokeLink = async (g) => {
    if (!window.confirm(`Révoquer le lien de ${g.prenom} ${g.nom} ?`)) return;
    try {
      await api.post(`/next/myevent/${eventId}/guests/${g.id}/magic-link/revoke`);
      toast.success("Lien révoqué.");
      if (lastLink?.guest_id === g.id) setLastLink(null);
      await load();
    } catch { toast.error("Impossible de révoquer le lien."); }
  };

  const copyToClipboard = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Lien copié."); }
    catch { toast.error("Copie impossible."); }
  };

  const linkBadge = (g) => {
    if (g.magic_token_revoked) return { label: "Lien révoqué", cls: "text-red-300/70 border-red-300/30 bg-red-300/5" };
    if (!g.magic_token_hash) return null;
    if (g.rsvp_updated_via_link_at) return { label: "Répondu via lien", cls: "text-emerald-300 border-emerald-300/40 bg-emerald-300/10" };
    if (g.magic_token_sent_at) return { label: "Lien envoyé", cls: "text-[#DCB457]/90 border-[#DCB457]/40 bg-[#DCB457]/10" };
    return { label: "Lien créé", cls: "text-white/60 border-white/20 bg-white/5" };
  };

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (rsvpFilter) params.set("rsvp", rsvpFilter);
    try {
      const [gRes, sRes] = await Promise.all([
        api.get(`/next/myevent/${eventId}/guests?${params}`),
        api.get(`/next/myevent/${eventId}/guests/stats`),
      ]);
      setGuests(gRes.data.guests || []);
      setStats(sRes.data);
    } catch (e) { setErr(e?.response?.status || 500); }
  };

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    api.get(`/next/myevent/${eventId}/dashboard`).then(r => setDashboard(r.data)).catch(() => {});
    load().finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => {
    if (eventId) load();
    // eslint-disable-next-line
  }, [q, rsvpFilter]);

  if (!user) return <Navigate to="/connexion" replace />;
  if (err === 403) return <ErrorScreen title="Accès refusé" text="Vous n'êtes pas membre de cet événement." testid="myguests-forbidden" />;
  if (err === 404) return <ErrorScreen title="Événement introuvable" text="Cet événement n'existe pas." testid="myguests-notfound" />;

  const openCreate = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (g) => { setForm({ ...EMPTY, ...g }); setEditing(g.id); };
  const closeForm = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.prenom.trim() || !form.nom.trim()) { toast.error("Prénom et nom obligatoires."); return; }
    setSaving(true);
    try {
      const body = {
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        email: form.email?.trim() || null,
        phone: form.phone?.trim() || null,
        rsvp_status: form.rsvp_status || "pending",
        adults: Number(form.adults) || 0,
        children: Number(form.children) || 0,
        category: form.category?.trim() || null,
        notes: form.notes?.trim() || null,
      };
      if (editing === "new") {
        await api.post(`/next/myevent/${eventId}/guests`, body);
        toast.success("Invité ajouté.");
      } else {
        await api.patch(`/next/myevent/${eventId}/guests/${editing}`, body);
        toast.success("Invité modifié.");
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la sauvegarde.");
    } finally { setSaving(false); }
  };

  const remove = async (g) => {
    if (!window.confirm(`Supprimer ${g.prenom} ${g.nom} ?`)) return;
    try {
      await api.delete(`/next/myevent/${eventId}/guests/${g.id}`);
      toast.success("Invité supprimé.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
  };

  const ev = dashboard?.event || {};
  const couple = cleanName(ev.name);

  return (
    <div className="min-h-screen bg-black text-white pb-20" data-testid="myevent-guests">
      <header className="border-b border-[#DCB457]/15 bg-black/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to={`/myevent/${eventId}`} data-testid="guests-back" className="text-xs uppercase tracking-widest text-white/60 hover:text-[#DCB457] inline-flex items-center gap-2">
            <ArrowLeft size={13} /> MyEvent
          </Link>
          <span className="text-[10px] uppercase tracking-[0.24em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-1">MyGuests</span>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-8 lg:pt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <MyEventSidebar />

        <main className="lg:col-span-9 min-w-0 space-y-8">
          {/* Titre */}
          <section>
            <p className="eyebrow">MyEvent · Module</p>
            <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Vos <span className="italic gold-text">invités.</span></h1>
            <p className="mt-3 text-white/60 text-sm md:text-base">Organisez facilement votre liste d'invités.</p>
            {ev.date && <p className="mt-1 text-[#DCB457] text-sm" data-testid="guests-event-summary">{couple} — {fmtDate(ev.date)}</p>}
          </section>

          {/* Stats */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="guests-stats">
            <StatCard label="Total" value={stats?.total ?? 0} icon={Users} />
            <StatCard label="Confirmés" value={stats?.confirmed ?? 0} accent />
            <StatCard label="En attente" value={stats?.pending ?? 0} />
            <StatCard label="Refusés" value={stats?.declined ?? 0} />
          </section>
          {stats && (stats.adults + stats.children) > 0 && (
            <p className="text-[10px] uppercase tracking-widest text-white/50" data-testid="guests-persons-line">
              Personnes attendues : <span className="text-[#DCB457]">{stats.adults}</span> adulte{stats.adults > 1 ? "s" : ""} · <span className="text-[#DCB457]">{stats.children}</span> enfant{stats.children > 1 ? "s" : ""}
            </p>
          )}

          {/* Actions */}
          <section className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="relative flex-1 max-w-md">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#DCB457]/60" />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher par nom, prénom ou email…"
                data-testid="guests-search"
                className="w-full bg-black/60 border border-[#DCB457]/25 pl-9 pr-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#DCB457] focus:outline-none" />
            </div>
            <select value={rsvpFilter} onChange={(e) => setRsvpFilter(e.target.value)}
              data-testid="guests-rsvp-filter"
              className="bg-black/60 border border-[#DCB457]/25 px-3 py-2.5 text-sm text-white focus:border-[#DCB457] focus:outline-none">
              <option value="">Tous les statuts</option>
              <option value="confirmed">Confirmés</option>
              <option value="pending">En attente</option>
              <option value="declined">Refusés</option>
            </select>
            <button onClick={openCreate} data-testid="guests-add-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#DCB457] text-black hover:bg-[#DCB457]/90 text-xs uppercase tracking-widest">
              <Plus size={13} /> Ajouter un invité
            </button>
          </section>

          {/* Liste */}
          <section data-testid="guests-list">
            {loading ? (
              <p className="text-white/50 italic text-sm">Chargement…</p>
            ) : guests.length === 0 ? (
              <div className="card-luxe p-8 text-center">
                <p className="text-white/60 text-sm mb-4">{q || rsvpFilter ? "Aucun résultat." : "Aucun invité pour l'instant."}</p>
                {!q && !rsvpFilter && (
                  <button onClick={openCreate} className="text-[#DCB457] text-xs uppercase tracking-widest hover:underline">
                    Ajouter votre premier invité
                  </button>
                )}
              </div>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {guests.map(g => {
                  const rc = RSVP_COLORS[g.rsvp_status] || RSVP_COLORS.pending;
                  const Icon = rc.icon;
                  return (
                    <li key={g.id} data-testid={`guest-${g.id}`}
                      className="card-luxe p-4 hover:border-[#DCB457] transition group">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openEdit(g)}>
                          <p className="font-serif-luxe text-lg leading-tight truncate">{g.prenom} {g.nom}</p>
                          {g.category && <p className="text-[10px] uppercase tracking-widest text-white/45 mt-0.5">{g.category}</p>}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 border ${rc.chip}`}>
                              <Icon size={10} strokeWidth={1.6} /> {RSVP_LABELS[g.rsvp_status]}
                            </span>
                            <span className="text-[10px] text-white/50">
                              {g.adults} adulte{g.adults > 1 ? "s" : ""}{g.children > 0 && ` + ${g.children} enfant${g.children > 1 ? "s" : ""}`}
                            </span>
                            {(() => { const b = linkBadge(g); return b ? (
                              <span data-testid={`guest-link-badge-${g.id}`}
                                className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-2 py-0.5 border ${b.cls}`}>
                                <Link2 size={9} /> {b.label}
                              </span>
                            ) : null; })()}
                          </div>
                          {(g.email || g.phone) && (
                            <p className="text-[11px] text-white/40 mt-2 truncate">
                              {g.email}{g.email && g.phone ? " · " : ""}{g.phone}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); setLinkGuestId(linkGuestId === g.id ? null : g.id); }}
                            data-testid={`guest-link-${g.id}`}
                            title="Gérer le lien invité"
                            className="text-[#DCB457]/70 hover:text-[#DCB457] transition p-1">
                            <Link2 size={13} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); remove(g); }}
                            data-testid={`guest-delete-${g.id}`}
                            className="text-red-300/60 hover:text-red-300 opacity-0 group-hover:opacity-100 transition p-1">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>

                      {linkGuestId === g.id && (
                        <div className="mt-3 pt-3 border-t border-[#DCB457]/15 space-y-2" data-testid={`guest-link-panel-${g.id}`}>
                          {lastLink?.guest_id === g.id && lastLink.url && (
                            <div className="bg-black/50 border border-[#DCB457]/20 p-2 flex items-center gap-2">
                              <input readOnly value={lastLink.url} data-testid={`guest-link-url-${g.id}`}
                                className="flex-1 bg-transparent text-[11px] text-white/70 outline-none min-w-0" />
                              <button onClick={() => copyToClipboard(lastLink.url)}
                                data-testid={`guest-link-copy-${g.id}`}
                                className="text-[#DCB457] hover:text-[#e6c454] p-1"><Copy size={12} /></button>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2">
                            <button
                              disabled={linkLoading}
                              onClick={(e) => { e.stopPropagation(); generateLink(g, false); }}
                              data-testid={`guest-link-generate-${g.id}`}
                              className="text-[10px] uppercase tracking-widest px-2.5 py-1 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10 disabled:opacity-40 inline-flex items-center gap-1.5">
                              <Link2 size={10} /> {g.magic_token_hash ? "Régénérer" : "Créer le lien"}
                            </button>
                            {g.email && (
                              <button
                                disabled={linkLoading}
                                onClick={(e) => { e.stopPropagation(); generateLink(g, true); }}
                                data-testid={`guest-link-email-${g.id}`}
                                className="text-[10px] uppercase tracking-widest px-2.5 py-1 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10 disabled:opacity-40 inline-flex items-center gap-1.5">
                                <MailPlus size={10} /> Envoyer par email
                              </button>
                            )}
                            {g.magic_token_hash && !g.magic_token_revoked && (
                              <button
                                onClick={(e) => { e.stopPropagation(); revokeLink(g); }}
                                data-testid={`guest-link-revoke-${g.id}`}
                                className="text-[10px] uppercase tracking-widest px-2.5 py-1 border border-red-300/40 text-red-300 hover:bg-red-300/10 inline-flex items-center gap-1.5">
                                <Ban size={10} /> Révoquer
                              </button>
                            )}
                          </div>
                          {g.magic_token_expires_at && (
                            <p className="text-[10px] text-white/40 italic">Expire le {fmtDate(g.magic_token_expires_at)}</p>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </main>
      </div>

      {/* Modal édition / création */}
      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-4 md:p-6" data-testid="guest-form-modal">
          <div className="card-luxe max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 md:p-8 relative">
            <button onClick={closeForm} className="absolute top-4 right-4 text-white/40 hover:text-[#DCB457]" data-testid="guest-form-close">
              <X size={16} />
            </button>
            <p className="eyebrow">{editing === "new" ? "Nouvel invité" : "Modifier"}</p>
            <h2 className="font-serif-luxe text-2xl mt-2 mb-6">{editing === "new" ? "Ajouter un invité" : `${form.prenom} ${form.nom}`}</h2>

            <div className="space-y-3">
              <Row2>
                <Field label="Prénom *" value={form.prenom} testid="form-prenom" onChange={(v) => setForm(f => ({ ...f, prenom: v }))} />
                <Field label="Nom *" value={form.nom} testid="form-nom" onChange={(v) => setForm(f => ({ ...f, nom: v }))} />
              </Row2>
              <Field label="Email" value={form.email || ""} testid="form-email" type="email" onChange={(v) => setForm(f => ({ ...f, email: v }))} />
              <Field label="Téléphone" value={form.phone || ""} testid="form-phone" onChange={(v) => setForm(f => ({ ...f, phone: v }))} />
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-white/50 mb-1">Statut RSVP</label>
                <select value={form.rsvp_status} onChange={(e) => setForm(f => ({ ...f, rsvp_status: e.target.value }))}
                  data-testid="form-rsvp"
                  className="w-full bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm text-white focus:border-[#DCB457] focus:outline-none">
                  <option value="pending">En attente</option>
                  <option value="confirmed">Confirmé</option>
                  <option value="declined">Refusé</option>
                </select>
              </div>
              <Row2>
                <Field label="Adultes" type="number" value={form.adults} testid="form-adults" onChange={(v) => setForm(f => ({ ...f, adults: v }))} min="0" max="20" />
                <Field label="Enfants" type="number" value={form.children} testid="form-children" onChange={(v) => setForm(f => ({ ...f, children: v }))} min="0" max="20" />
              </Row2>
              <Field label="Catégorie" placeholder="ex. Famille, Amis proches…" value={form.category || ""} testid="form-category" onChange={(v) => setForm(f => ({ ...f, category: v }))} />
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-white/50 mb-1">Notes</label>
                <textarea value={form.notes || ""} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  data-testid="form-notes" rows={3}
                  className="w-full bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm text-white/85 focus:border-[#DCB457] focus:outline-none" />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} disabled={saving} data-testid="guest-save"
                className="flex-1 px-4 py-2.5 bg-[#DCB457] text-black hover:bg-[#DCB457]/90 text-xs uppercase tracking-widest disabled:opacity-50">
                {saving ? "Enregistrement…" : (editing === "new" ? "Ajouter" : "Enregistrer")}
              </button>
              <button onClick={closeForm} className="px-4 py-2.5 border border-white/20 text-white/70 hover:text-[#DCB457] text-xs uppercase tracking-widest">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="card-luxe p-4">
      <p className="eyebrow">{label}</p>
      <p className={`font-serif-luxe text-3xl mt-2 ${accent ? "gold-text" : ""}`}>{value}</p>
    </div>
  );
}
function Row2({ children }) { return <div className="grid grid-cols-2 gap-3">{children}</div>; }
function Field({ label, value, onChange, testid, type = "text", placeholder, min, max }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-white/50 mb-1">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testid} placeholder={placeholder || ""} min={min} max={max}
        className="w-full bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm text-white focus:border-[#DCB457] focus:outline-none" />
    </div>
  );
}
function ErrorScreen({ title, text, testid }) {
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
      <div className="max-w-md text-center" data-testid={testid}>
        <p className="eyebrow">MyGuests</p>
        <h1 className="font-serif-luxe text-3xl mt-3 mb-3">{title}</h1>
        <p className="text-white/60 text-sm">{text}</p>
      </div>
    </div>
  );
}
